/**
 * Reviewer status for the Exposed Sensitive Documents page (design spec 9.3, 9.4).
 *
 * A *status* is a reviewer's claim about one row: `open`, `acknowledged`, `resolved` or
 * `dismissed`. `open` is the absence of a record; the other three are stored one document per
 * row under `organisations/{org}/exposed_sensitive_documents_status/{rowId}`, the id being the
 * row id. `reopened` is never stored: the page derives it from a `resolved` record set in an
 * earlier generation (its `publishedRunId`) than the one now listing the row.
 *
 * Here are the record parser, the client-side join, the reopened rule, the attention rank and
 * the status a row displays. Like the page view model, nothing here may import firebase, a
 * store, `$lib/utils/env` or a `.svelte` file.
 */
import type { ExposedSensitiveDocument } from "../exposedSensitiveDocuments";
import { parseDateValue } from "./dateValue";

/** The statuses a record can hold. `open` is the absence of a record, so it is not one of them. */
export type StoredStatus = "acknowledged" | "resolved" | "dismissed";
const STORED_STATUSES: readonly StoredStatus[] = ["acknowledged", "resolved", "dismissed"];

/** A status a row can display: the three stored ones, `open`, and the derived `reopened`. */
export type RowStatus = "reopened" | "open" | StoredStatus;

/**
 * The attention rank: most in need of a reviewer first. One ordered constant for the whole page;
 * the status filter's option order and the Status sort both read it.
 */
export const ATTENTION_RANK: readonly RowStatus[] = ["reopened", "open", "acknowledged", "resolved", "dismissed"];

/** `statuses` in attention order, duplicates and unknown values dropped. */
export function inAttentionOrder(statuses: readonly string[]): RowStatus[] {
  return ATTENTION_RANK.filter((status) => statuses.includes(status));
}

export const STATUS_LABELS: Record<RowStatus, string> = {
  reopened: "Reopened",
  open: "Open",
  acknowledged: "Acknowledged",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

/**
 * What a row shows when the status read failed: "—" with the tooltip "Status unavailable",
 * never `open`. Not a status and never ranked.
 */
export const STATUS_UNAVAILABLE = "unavailable";
/** The words for {@link STATUS_UNAVAILABLE}: the table cell's tooltip and the panel block's line. */
export const STATUS_UNAVAILABLE_LABEL = "Status unavailable";
/** The glyph for {@link STATUS_UNAVAILABLE}: the table cell and the panel block's status picker. */
export const STATUS_UNAVAILABLE_GLYPH = "—";
export type DisplayedStatus = RowStatus | typeof STATUS_UNAVAILABLE;

/** One status record, the camelCase fields the route writes (`ExposedSensitiveDocumentStatusRecord`). */
export interface StatusRecord {
  status: StoredStatus;
  note?: string;
  /** Who set it. May be `""`. */
  updatedBy: string;
  updatedAt?: Date;
  /** The generation the status was set in; `""` when absent, which is never reopened. */
  publishedRunId: string;
}

/** A row with its status record joined on, or without one when it is `open`. */
export type ExposedDocumentWithStatus = ExposedSensitiveDocument & { status?: StatusRecord };

/** Where the status read is: rows read `open` while `loading`, and unavailable once `failed`. */
export type StatusLoadState = "loading" | "ready" | "failed";

/**
 * One raw record -> a {@link StatusRecord}, or `undefined` for a status this build does not
 * know. Accepts both wire shapes of `updatedAt`: the route's ISO string and the Firestore
 * `Timestamp` a `getDocs` returns.
 */
export function parseStatusRecord(data: unknown): StatusRecord | undefined {
  const raw = (data ?? {}) as Record<string, unknown>;
  const status = STORED_STATUSES.find((known) => known === raw.status);
  if (!status) return undefined;
  const note = typeof raw.note === "string" && raw.note !== "" ? raw.note : undefined;
  return {
    status,
    ...(note === undefined ? {} : { note }),
    updatedBy: typeof raw.updatedBy === "string" ? raw.updatedBy : "",
    updatedAt: parseDateValue(raw.updatedAt),
    publishedRunId: typeof raw.publishedRunId === "string" ? raw.publishedRunId : "",
  };
}

/**
 * Join records onto rows by id. A row without a record carries no `status` (it is `open`); a
 * record without a row cannot exist, and if one ever does it is simply never looked up.
 */
export function joinStatusRecords(
  rows: readonly ExposedSensitiveDocument[],
  records: ReadonlyMap<string, StatusRecord>,
): ExposedDocumentWithStatus[] {
  if (records.size === 0) return [...rows];
  return rows.map((row) => {
    const status = records.get(row.id);
    return status ? { ...row, status } : row;
  });
}

/**
 * The reopened rule: a `resolved` record set in a generation (its `publishedRunId`) other than
 * the one now listing the row. Generations are compared for equality only, never ordered, and
 * a record with no generation is never reopened. `acknowledged` and `dismissed` are never reopened.
 */
export function isReopened(row: Pick<ExposedSensitiveDocument, "publishedRunId">, record?: StatusRecord): boolean {
  return record?.status === "resolved" && record.publishedRunId !== "" && record.publishedRunId !== row.publishedRunId;
}

/** The status a row ranks, filters and sorts by: `open` without a record, `reopened` by the rule above. */
export function rowStatusOf(row: ExposedDocumentWithStatus): RowStatus {
  if (!row.status) return "open";
  return isReopened(row, row.status) ? "reopened" : row.status.status;
}

/** What the Status column shows: the row's status, `open` while loading, unavailable once the read failed. */
export function displayedStatusOf(row: ExposedDocumentWithStatus, loadState: StatusLoadState): DisplayedStatus {
  if (loadState === "failed") return STATUS_UNAVAILABLE;
  return rowStatusOf(row);
}

// ---------------------------------------------------------------------------------------------
// Filter, counts and sort (spec 9.5)
// ---------------------------------------------------------------------------------------------

/**
 * `?status=` -> the statuses picked, in the order written: comma-separated, trimmed, duplicates
 * collapsed keeping the first, unknown values dropped - the `?infoTypes=` normalisation. `open`
 * and `reopened` are pickable although neither is stored. Absent, empty, or nothing known left is
 * the empty list, which means **all statuses**: unlike `?infoTypes=` there is no org default, so
 * there is no third state to keep apart.
 */
export function decodeStatusParam(value: string | null | undefined): RowStatus[] {
  if (!value) return [];
  const names = new Set(value.split(",").map((name) => name.trim()));
  return [...names].filter((name): name is RowStatus => (ATTENTION_RANK as readonly string[]).includes(name));
}

/**
 * The status filter. An empty selection matches every row. A failed read makes the whole chain
 * **status-blind**: the filter matches every row whatever the URL says, so neither the table nor
 * the cards narrow (spec 9.3, "rows, cards, pills and the two exits are unaffected"; the select is
 * disabled), and a row is never kept or dropped as `open` on a read that did not land. While
 * `loading`, rows carry no record yet and match as `open`.
 */
export function rowMatchesStatus(
  row: ExposedDocumentWithStatus,
  selected: readonly RowStatus[],
  loadState: StatusLoadState,
): boolean {
  if (loadState === "failed" || selected.length === 0) return true;
  return selected.includes(rowStatusOf(row));
}

/** {@link rowMatchesStatus} over wrapped rows, the chain's step between info type and exposure. */
export function filterRowsByStatus<T extends { row: ExposedDocumentWithStatus }>(
  rows: readonly T[],
  selected: readonly RowStatus[],
  loadState: StatusLoadState,
): T[] {
  return rows.filter((wrapped) => rowMatchesStatus(wrapped.row, selected, loadState));
}

/** One option's count on the status filter. */
export interface StatusCount {
  status: RowStatus;
  count: number;
}

/**
 * The per-status counts on the filter's options: every status in attention order, zero-filled.
 * The caller passes the rows after provider and info type but before status and exposure, so the
 * values sum to the cards' union. Nothing to show on a failed read, where the select is disabled.
 */
export function countByStatus(rows: readonly ExposedDocumentWithStatus[]): StatusCount[] {
  const counts = new Map<RowStatus, number>(ATTENTION_RANK.map((status) => [status, 0]));
  for (const row of rows) {
    const status = rowStatusOf(row);
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return ATTENTION_RANK.map((status) => ({ status, count: counts.get(status) ?? 0 }));
}

/** The Status sort key: the row's place in {@link ATTENTION_RANK}, `reopened` = 0. */
export function statusRankOf(row: ExposedDocumentWithStatus): number {
  return ATTENTION_RANK.indexOf(rowStatusOf(row));
}
